import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError } from "../web/src/lib/api.js";
import type { GuestbookEntry, GuestbookListResponse, GuestbookStatus } from "@shared";

export interface GuestbookProps {
  /** Event slug. */
  readonly slug: string;
  /** The guest's link code, when the composer belongs to a guest page. */
  readonly guestCode?: string;
  /** Per-browser key that lets a guest delete their own note later. */
  readonly guestKey?: string;
  /** The event password, when the guest holds one. */
  readonly password?: string;
  /** Host mode: the session token is already in api headers; enables moderation. */
  readonly isAdmin?: boolean;
  readonly onCountChange?: (total: number) => void;
  readonly compact?: boolean;
  /** Translated strings from lib/i18n. Host mounts stay English. */
  readonly labels?: GuestbookLabels;
  /** Timestamps in the guest's language; the browser default otherwise. */
  readonly formatDate?: (iso: string) => string;
}

export interface GuestbookLabels {
  readonly title?: string;
  readonly composePlaceholder?: string;
  readonly namePlaceholder?: string;
  readonly send?: string;
  readonly sending?: string;
  readonly record?: string;
  readonly stop?: string;
  readonly empty?: string;
  readonly removeMine?: string;
  readonly waitingForHost?: string;
  readonly heldNotice?: string;
  readonly previewHint?: string;
  readonly sendVoice?: string;
  readonly recordAgain?: string;
  readonly discard?: string;
  readonly micDenied?: string;
  readonly showMore?: string;
}

const MAX_TEXT = 500;
/** Matches LIMITS.maxGuestbookAudioSeconds; the server refuses anything longer. */
const MAX_RECORD_SECONDS = 60;
const PAGE = 50;

type HostTab = "pending" | "approved" | "rejected" | "all";

const HOST_TABS: readonly { readonly value: HostTab; readonly label: string }[] = [
  { value: "pending", label: "Waiting" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

/** A finished recording held for the guest to hear before it is sent. */
interface Take {
  readonly blob: Blob;
  readonly mime: string;
  readonly seconds: number;
  readonly url: string;
}

/**
 * The guestbook: text notes and voice notes guests leave for the hosts.
 *
 * Two callers share one component. A guest page mounts it without `isAdmin`,
 * passing its device key. The host's control page mounts it with `isAdmin`,
 * which turns it into a moderation queue: waiting / approved / rejected, with
 * approve, reject and delete on every note.
 *
 * A voice note is recorded, played back, and only then sent — re-recording is
 * one tap — and the recorder stops itself at the server's sixty-second cap.
 */
export function Guestbook({
  slug,
  guestCode,
  guestKey,
  password,
  isAdmin = false,
  onCountChange,
  compact = false,
  labels = {},
  formatDate = (iso: string) => new Date(iso).toLocaleString(),
}: GuestbookProps) {
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<GuestbookStatus, number> | null>(null);
  const [tab, setTab] = useState<HostTab>("pending");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [take, setTake] = useState<Take | null>(null);

  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number | null>(null);
  const startedAt = useRef(0);
  const autoStop = useRef<number | null>(null);

  const buildQuery = useCallback(
    (extra: Record<string, string> = {}): string => {
      const params = new URLSearchParams(extra);
      if (password) params.set("password", password);
      if (guestCode) params.set("guestCode", guestCode);
      if (guestKey) params.set("likerKey", guestKey);
      const suffix = params.toString();
      return suffix ? `?${suffix}` : "";
    },
    [password, guestCode, guestKey],
  );

  const load = useCallback(
    async (limit = PAGE): Promise<void> => {
      try {
        // The credentials ride the list query too, so the server can tag each
        // entry `mine` and include this browser's own held notes.
        const query = isAdmin
          ? `?${new URLSearchParams({ status: tab, limit: String(limit) }).toString()}`
          : buildQuery({ limit: String(limit) });
        const result = await api.get<GuestbookListResponse>(`/api/events/${slug}/guestbook${query}`);
        setEntries([...result.entries]);
        setTotal(result.total);
        setCounts(result.counts ? { ...result.counts } : null);
        onCountChange?.(result.total);
        setNotice("");
      } catch (err) {
        if (err instanceof ApiError && err.status !== 401) setNotice(err.message);
      }
    },
    [slug, isAdmin, tab, buildQuery, onCountChange],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /** Delete one note: the author on the guest side, the host on the host side. */
  const removeEntry = async (entryId: string): Promise<void> => {
    if (!window.confirm("Remove this note?")) return;
    setBusy(true);
    try {
      if (isAdmin) await api.del(`/api/events/${slug}/guestbook/${entryId}`);
      else await api.del(`/api/guestbook/${entryId}${buildQuery()}`, { guestCode, likerKey: guestKey });
      await load(Math.max(PAGE, entries.length));
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : "Could not remove that note.");
    } finally {
      setBusy(false);
    }
  };

  const decide = async (entryId: string, status: GuestbookStatus): Promise<void> => {
    setBusy(true);
    try {
      await api.patch(`/api/events/${slug}/guestbook/${entryId}`, { status });
      await load(Math.max(PAGE, entries.length));
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : "Could not update that note.");
    } finally {
      setBusy(false);
    }
  };

  /** Tell the guest what happened to the note they just sent. */
  const afterPost = (entry: GuestbookEntry | undefined): void => {
    setNotice(
      entry?.status === "pending"
        ? (labels.heldNotice ?? "Thank you! Your note will appear once the host has seen it.")
        : "",
    );
  };

  /** Post the text box as a note. */
  const submitText = async (): Promise<void> => {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const result = await api.post<{ entry: GuestbookEntry }>(`/api/events/${slug}/guestbook`, {
        body: text,
        guestName: name.trim(),
        guestCode,
        likerKey: guestKey,
      });
      setBody("");
      await load();
      afterPost(result.entry);
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : "Could not send that note.");
    } finally {
      setBusy(false);
    }
  };

  const canRecord =
    typeof MediaRecorder !== "undefined" && typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  const clearTimers = (): void => {
    if (autoStop.current) window.clearTimeout(autoStop.current);
    if (timer.current) window.clearInterval(timer.current);
    autoStop.current = null;
    timer.current = null;
  };

  const discardTake = useCallback((): void => {
    setTake((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  }, []);

  const stopRecording = (): void => {
    clearTimers();
    setRecording(false);
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
    recorder.current = null;
  };

  /** Start a voice note. The browser's own encoder decides WebM or MP4. */
  const startRecording = async (): Promise<void> => {
    if (recording || busy) return;
    discardTake();
    setNotice("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      rec.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.current.push(event.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunks.current, { type: mime });
        const seconds = Math.min(MAX_RECORD_SECONDS, Math.max(1, Math.round((Date.now() - startedAt.current) / 1000)));
        setTake({ blob, mime, seconds, url: URL.createObjectURL(blob) });
      };
      recorder.current = rec;
      rec.start();
      startedAt.current = Date.now();
      setRecording(true);
      setElapsed(0);

      timer.current = window.setInterval(() => {
        setElapsed(Math.min(MAX_RECORD_SECONDS * 1000, Date.now() - startedAt.current));
      }, 250);
      // Stop at the cap rather than let the server refuse a note the guest
      // has no way to shorten.
      autoStop.current = window.setTimeout(() => stopRecording(), MAX_RECORD_SECONDS * 1000);
    } catch (error) {
      const denied = error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError");
      setNotice(
        denied
          ? (labels.micDenied ?? "Microphone access was blocked. Allow it in your browser's site settings, then try again.")
          : "The microphone could not be opened on this device.",
      );
    }
  };

  /** POST the held recording; the body is the request. */
  const sendTake = async (): Promise<void> => {
    if (!take || busy) return;
    setBusy(true);
    try {
      const query = buildQuery({ guestName: name.trim(), durationSec: String(take.seconds) });
      const response = await fetch(`/api/events/${slug}/guestbook/audio${query}`, {
        method: "POST",
        headers: { "content-type": take.mime },
        body: take.blob,
      });
      const payload = (await response.json().catch(() => ({}))) as {
        entry?: GuestbookEntry;
        error?: { message?: string };
      };
      if (!response.ok) throw new Error(payload.error?.message ?? `Sending failed (${response.status}).`);
      discardTake();
      await load();
      afterPost(payload.entry);
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Could not send that voice note.");
    } finally {
      setBusy(false);
    }
  };

  // Unmounting a live recorder keeps the OS microphone indicator lit forever.
  useEffect(() => {
    return () => {
      clearTimers();
      if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
    };
  }, []);

  useEffect(() => () => discardTake(), [discardTake]);

  const remaining = MAX_RECORD_SECONDS - Math.floor(elapsed / 1000);

  return (
    <section className={`guestbook stack${compact ? " guestbook-compact" : ""}`}>
      <header className="row" style={{ justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>{labels.title ?? "Guestbook"}</h3>
        {total > 0 && !isAdmin ? <span className="muted">{total}</span> : null}
        <button type="button" className="link-button" onClick={() => void load(Math.max(PAGE, entries.length))}>
          Refresh
        </button>
      </header>

      {isAdmin ? (
        <div className="row guestbook-tabs" role="tablist" aria-label="Guestbook notes by status">
          {HOST_TABS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={tab === option.value}
              className={tab === option.value ? "tag-chip tag-chip-active" : "tag-chip"}
              onClick={() => setTab(option.value)}
            >
              {option.label}
              {counts
                ? ` (${option.value === "all" ? counts.pending + counts.approved + counts.rejected : counts[option.value]})`
                : ""}
            </button>
          ))}
        </div>
      ) : null}

      {notice ? (
        <p className="notice muted" role="status">
          {notice}
        </p>
      ) : null}

      {!isAdmin && (guestCode || guestKey) ? (
        <div className="card stack" style={{ padding: "1rem" }}>
          <label htmlFor="guestbook-body" style={{ fontWeight: 600 }}>
            {labels.composePlaceholder ?? "Leave a note for the hosts"}
          </label>
          <textarea
            id="guestbook-body"
            value={body}
            maxLength={MAX_TEXT}
            rows={3}
            placeholder={labels.composePlaceholder ?? "Congratulations, a story, a thank-you…"}
            onChange={(event) => setBody(event.target.value)}
            style={{ resize: "vertical", fontSize: "16px" }}
          />
          <input
            type="text"
            value={name}
            maxLength={40}
            placeholder={labels.namePlaceholder ?? "Your name (optional)"}
            onChange={(event) => setName(event.target.value)}
            style={{ fontSize: "16px" }}
          />
          <div className="row" style={{ flexWrap: "wrap" }}>
            <button type="button" onClick={() => void submitText()} disabled={busy || !body.trim()}>
              {busy && !take ? (labels.sending ?? "Sending…") : (labels.send ?? "Send note")}
            </button>
            {canRecord && !take ? (
              recording ? (
                <button type="button" className="button-danger" onClick={stopRecording}>
                  {labels.stop ?? "Stop"} ({remaining}s)
                </button>
              ) : (
                <button type="button" className="button-secondary" onClick={() => void startRecording()} disabled={busy}>
                  {labels.record ?? "Record a voice note"}
                </button>
              )
            ) : null}
          </div>

          {take ? (
            <div className="guestbook-take stack" style={{ gap: "0.5rem" }}>
              <span className="muted">{labels.previewHint ?? "Listen back before you send it."}</span>
              <audio controls src={take.url} />
              <div className="row" style={{ flexWrap: "wrap" }}>
                <button type="button" onClick={() => void sendTake()} disabled={busy}>
                  {busy ? (labels.sending ?? "Sending…") : (labels.sendVoice ?? "Send voice note")}
                </button>
                <button type="button" className="button-secondary" onClick={() => void startRecording()} disabled={busy}>
                  {labels.recordAgain ?? "Record again"}
                </button>
                <button type="button" className="link-button" onClick={discardTake} disabled={busy}>
                  {labels.discard ?? "Discard"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {entries.length === 0 ? (
        <p className="muted">
          {isAdmin && tab === "pending"
            ? "Nothing waiting. New notes appear here when the event requires approval."
            : (labels.empty ?? "No notes yet — be the first.")}
        </p>
      ) : (
        <ul className="guestbook-list">
          {entries.map((entry) => (
            <li key={entry.entryId} className="guestbook-entry" data-entry-id={entry.entryId} data-status={entry.status}>
              <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem" }}>
                <span style={{ fontWeight: 600 }}>{entry.guestName || "A guest"}</span>
                <span className="muted" style={{ fontSize: "0.85rem" }}>
                  {formatDate(entry.createdAt)}
                </span>
              </div>
              {entry.status !== "approved" && (isAdmin || entry.mine) ? (
                <span className="badge">
                  {entry.status === "pending" ? (labels.waitingForHost ?? "Waiting for the host") : "Rejected"}
                </span>
              ) : null}
              {entry.kind === "text" ? (
                <p className="guestbook-body" style={{ margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {entry.body}
                </p>
              ) : (
                <audio controls preload="metadata" src={entry.audioUrl ?? ""} />
              )}
              <div className="row" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
                {isAdmin && entry.status !== "approved" ? (
                  <button type="button" className="link-button" onClick={() => void decide(entry.entryId, "approved")} disabled={busy}>
                    Approve
                  </button>
                ) : null}
                {isAdmin && entry.status !== "rejected" ? (
                  <button type="button" className="link-button" onClick={() => void decide(entry.entryId, "rejected")} disabled={busy}>
                    Reject
                  </button>
                ) : null}
                {isAdmin || entry.mine ? (
                  <button
                    type="button"
                    className="link-button"
                    style={{ color: "var(--muted, #777)" }}
                    onClick={() => void removeEntry(entry.entryId)}
                    disabled={busy}
                  >
                    {isAdmin ? "Delete" : (labels.removeMine ?? "Remove my note")}
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {entries.length < total ? (
        <button type="button" className="button-secondary" onClick={() => void load(entries.length + PAGE)} disabled={busy}>
          {labels.showMore ?? "Show more"}
        </button>
      ) : null}
    </section>
  );
}
